// A ÚNICA superfície que componente pode importar.
//
// Nenhum componente conhece `apiClientJourney`, `journey-source` ou `mocks/` —
// é o que torna a troca mock → HTTP invisível para a UI. As funções
// resolvem no shape final do contrato, inclusive no caminho de mock: latência
// simulada e caminho de erro existem para que loading e falha sejam exercitados
// antes do backend chegar, não depois.
import { apiClientJourney } from '@modules/client-journey/api/apiClientJourney'
import { JOURNEY_TEMPLATE_DEFAULT } from '@modules/client-journey/constants/journey-template-default'
import { jornadaMockDoCliente } from '@modules/client-journey/mocks/journey-clients'
import { overviewMockDoCliente } from '@modules/client-journey/mocks/journey-overview'
import {
  toClientJourney,
  toClientOverview,
  toClientJourneySummary,
  toJourneyIndicators,
  toJourneyTemplate,
} from '@modules/client-journey/services/journey-adapters'
import { isMockSource } from '@modules/client-journey/services/journey-source'

// Latência de mentira, para a UI de loading ser exercitada de verdade em
// desenvolvimento. Some junto com o mock.
const LATENCIA_MOCK_MS = 240

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function valorDaResposta(r) {
  if (!r) return null
  if (r.status && r.status >= 400) return null
  return r.value === undefined ? r : r.value
}

export async function getActiveTemplate() {
  if (isMockSource()) return JOURNEY_TEMPLATE_DEFAULT
  const r = await apiClientJourney.carregarTemplate()
  return toJourneyTemplate(valorDaResposta(r))
}

export async function getClientJourney(clientId) {
  if (!clientId) return null
  const template = await getActiveTemplate()

  if (isMockSource()) {
    await esperar(LATENCIA_MOCK_MS)
    return toClientJourney(jornadaMockDoCliente(clientId), template)
  }

  const r = await apiClientJourney.carregar({ clientId })
  const valor = valorDaResposta(r)
  return valor ? toClientJourney(valor, template) : null
}

export async function getClientOverview(clientId) {
  if (!clientId) return null

  if (isMockSource()) {
    await esperar(LATENCIA_MOCK_MS)
    return toClientOverview(overviewMockDoCliente(clientId))
  }

  const r = await apiClientJourney.carregarOverview({ clientId })
  return toClientOverview(valorDaResposta(r))
}

export async function getClientsJourneySummary(clientIds) {
  const ids = Array.isArray(clientIds) ? clientIds : []
  if (!ids.length) return []
  const template = await getActiveTemplate()

  if (isMockSource()) {
    await esperar(LATENCIA_MOCK_MS)
    return ids
      .map((id) => toClientJourneySummary(jornadaMockDoCliente(id), template))
      .filter(Boolean)
  }

  const r = await apiClientJourney.carregarResumos(ids)
  const valor = valorDaResposta(r)
  return (Array.isArray(valor) ? valor : [])
    .map((item) => toClientJourneySummary(item, template))
    .filter(Boolean)
}

// Recebe os resumos que a lista já tem em mão: no mock a conta é local, e com
// backend a assinatura não muda — só a origem do número.
export async function getIndicators(summaries) {
  if (isMockSource()) return toJourneyIndicators(summaries)
  const r = await apiClientJourney.carregarIndicadores()
  return valorDaResposta(r) || toJourneyIndicators(summaries)
}

export async function markStep(clientId, stepId, done = true) {
  const template = await getActiveTemplate()

  if (isMockSource()) {
    await esperar(LATENCIA_MOCK_MS)
    // O mock não persiste: devolve a jornada como está, e quem chamou já fez o
    // update otimista no store. É suficiente para exercitar o fluxo.
    return toClientJourney(jornadaMockDoCliente(clientId), template)
  }

  const r = await apiClientJourney.marcarPasso({ clientId, stepId, done })
  const valor = valorDaResposta(r)
  return valor ? toClientJourney(valor, template) : null
}
